import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { get } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { EditorialReviewEntities, FirstContentEntity } from 'client/data/models/editorial-review';
import { buildWhatsNewPath, VehicleDetailsEntities, VehicleDetailsModel } from 'client/data/models/mmy-details';
import { MENU_HASH } from 'site-modules/shared/constants/core-page/menu-hash';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { EditorialReviewBullets } from 'site-modules/shared/components/editorial-review-pros-cons/editorial-review-bullets';

import './editorial-highlights-lists.scss';

export const HIGHLIGHTS_NAME = {
  PROS: 'view_pros',
  CONS: 'view_cons',
  WHATS_NEW: 'view_whats_new',
  WHAT_TO_EXPECT: 'view_what_to_expect',
};

const LIST_ITEM_CLASS_NAME = 'ps-2 size-16 mb-0_5';
const CIRCLE_ICON_CLASS_NAME = 'icon-circle2 p-0_5 pt-0_5';

const MENU_HASH_BY_NAME = {
  [HIGHLIGHTS_NAME.WHATS_NEW]: MENU_HASH.WHATS_NEW,
};

export function EditorialHighlightsListsUI({
  editorialReview = null,
  firstContent = null,
  whatsNewContent = null,
  className = null,
  headingClassName = 'heading-4 mb-0_5',
  sectionsToRender = [
    HIGHLIGHTS_NAME.PROS,
    HIGHLIGHTS_NAME.CONS,
    HIGHLIGHTS_NAME.WHATS_NEW,
    HIGHLIGHTS_NAME.WHAT_TO_EXPECT,
  ],
  isPreprod = false,
}) {
  const cons = get(editorialReview, 'cons', []);
  const pros = get(editorialReview, 'pros', []);
  const hasHighlights = cons.length || pros.length || get(whatsNewContent, 'length');
  const whatToExpect = get(firstContent, 'bullets.value', []);

  const sections = hasHighlights
    ? [
        {
          name: HIGHLIGHTS_NAME.PROS,
          text: 'Pros',
          data: pros,
          iconClasses: 'icon-checkmark text-primary-darker size-12 ps-0_25 pt-0_25',
        },
        {
          name: HIGHLIGHTS_NAME.CONS,
          text: 'Cons',
          iconClasses: 'icon-cross2 text-danger size-12 ps-0_25 pt-0_25',
          data: cons,
        },
        {
          name: HIGHLIGHTS_NAME.WHATS_NEW,
          text: "What's new",
          data: whatsNewContent,
          iconClasses: CIRCLE_ICON_CLASS_NAME,
        },
      ]
    : [
        {
          name: HIGHLIGHTS_NAME.WHAT_TO_EXPECT,
          text: isPreprod ? 'What to expect' : 'What You Need to Know',
          data: whatToExpect,
          iconClasses: CIRCLE_ICON_CLASS_NAME,
        },
      ];

  const renderedSections = sections.filter(({ name, data }) => sectionsToRender.includes(name) && data?.length);

  if (!renderedSections.length) {
    return null;
  }

  return (
    <ScrollElement id={MENU_HASH.PROS_CONS} tag={Row} className={classnames('editorial-highlights-lists', className)}>
      {renderedSections.map(({ name, text, iconClasses, data }, index) => (
        <Col
          id={MENU_HASH_BY_NAME[name]}
          xs={12}
          md={[HIGHLIGHTS_NAME.PROS, HIGHLIGHTS_NAME.CONS].includes(name) ? 6 : undefined}
          className={classnames({
            'mt-1_5': index,
            'mt-md-0': [HIGHLIGHTS_NAME.PROS, HIGHLIGHTS_NAME.CONS].includes(name),
            'pe-md-1 border-md-end': name === HIGHLIGHTS_NAME.PROS,
            'ps-md-1': name === HIGHLIGHTS_NAME.CONS,
          })}
          key={`${name}-list`}
        >
          {!!Array.isArray(data) && (
            <Fragment>
              <h2 id={name} className={headingClassName}>
                {text}
              </h2>
              <ul className="list-unstyled mb-0" aria-labelledby={name} data-tracking-parent={name}>
                <EditorialReviewBullets
                  listItems={data}
                  listItemClassName={classnames('pos-r', LIST_ITEM_CLASS_NAME)}
                  listItemIconClasses={classnames('left-0', iconClasses)}
                  listItemStyles={{ paddingLeft: '2rem' }}
                />
              </ul>
            </Fragment>
          )}
        </Col>
      ))}
    </ScrollElement>
  );
}

EditorialHighlightsListsUI.propTypes = {
  firstContent: FirstContentEntity,
  editorialReview: EditorialReviewEntities.EditorialReview,
  whatsNewContent: VehicleDetailsEntities.WhatsNewContent,
  className: PropTypes.string,
  headingClassName: PropTypes.string,
  sectionsToRender: PropTypes.arrayOf(PropTypes.oneOf(Object.values(HIGHLIGHTS_NAME))),
  isPreprod: PropTypes.bool,
};

export const propsAreEqual = (prevProps, { editorialReview, firstContent }) => !(editorialReview || firstContent);
export const EditorialHighlightsLists = connectToModel(React.memo(EditorialHighlightsListsUI, propsAreEqual), {
  whatsNewContent: bindToPath(
    ({ params, sectionsToRender }) =>
      (!sectionsToRender || sectionsToRender.includes(HIGHLIGHTS_NAME.WHATS_NEW)) && buildWhatsNewPath(params),
    VehicleDetailsModel
  ),
});
